import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { FileText, Search, Loader2, PoundSterling, CheckCircle2, Zap, Tag, Info, Users, Sparkles, Star, Award } from 'lucide-react';
import axios from 'axios';
import { useLocalPrice } from '../lib/localCurrency';
import { formatCurrency } from '../lib/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PurchaseServicesPage() {
  const navigate = useNavigate();
  const { token, isAuthenticated, user, loading: authLoading } = useAuth();
  const localPrice = useLocalPrice(user?.country);
  const pricingUnavailable = localPrice.isLoading || !!localPrice.error ||
    (isAuthenticated && (authLoading || !user));
  const [checkingOut, setCheckingOut] = useState(null); // `${serviceId}-stripe` or `${serviceId}-paypal`
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [paypalEnabled, setPaypalEnabled] = useState(false);
  const [quantities, setQuantities] = useState({});
  const [discountCode, setDiscountCode] = useState('');
  const [discountMessage, setDiscountMessage] = useState('');
  const [bankEnabled, setBankEnabled] = useState(false);
  const [bankResult, setBankResult] = useState(null); // { reference, amount, instructions }
  const [catalog, setCatalog] = useState({});          // admin-editable prices by service_type
  const [pageText, setPageText] = useState({});        // admin-editable page wording (overrides built-in text)

  useEffect(() => {
    const fetchGatewayStatus = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/payments/gateways/public`);
        const gateways = res.data || [];
        setStripeEnabled(gateways.find(g => g.gateway === 'stripe')?.is_enabled === true);
        setPaypalEnabled(gateways.find(g => g.gateway === 'paypal')?.is_enabled === true);
        setBankEnabled(gateways.find(g => g.gateway === 'bank_transfer')?.is_enabled === true);
      } catch {
        // fallback: no payment buttons shown if endpoint unavailable
      }
    };
    const fetchCatalog = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/payments/services`);
        const map = {};
        (res.data || []).forEach(s => { map[s.service_type] = s; });
        setCatalog(map);
      } catch {
        // keep the built-in defaults below
      }
    };
    const fetchPageText = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/payments/services/page-text`);
        setPageText(t => ({ ...t, ...res.data }));
      } catch {
        // keep the built-in wording
      }
    };
    fetchGatewayStatus();
    fetchCatalog();
    fetchPageText();
  }, []);

  // Built-in defaults; name/description/price are overridden by the admin-managed catalog when available.
  const baseServices = [
    {
      id: 'job_advert_credit',
      name: 'Job Advert Credit',
      description: 'Premium visibility. Advertise your role to over 26 million candidates.',
      price: 89.00,
      icon: FileText,
      features: ['Premium job listing', 'Enhanced visibility', 'Standard position', '30-day duration'],
      color: 'bg-purple-500',
      category: 'advertising'
    },
    {
      id: 'featured_job_post',
      name: 'Featured Job Posting',
      description: 'Get your job to the top of search results for maximum visibility.',
      price: 129.00,
      icon: Zap,
      features: ['Featured position', 'Top search results', 'Priority display', '30-day duration'],
      color: 'bg-orange-500',
      category: 'advertising'
    },
    {
      id: 'cv_search',
      name: 'CV Search Access',
      description: 'Find talent faster with access to our candidate database.',
      price: 99.00,
      icon: Search,
      features: ['Database access', 'Advanced search filters', 'Export CVs', '30-day access'],
      color: 'bg-blue-500',
      category: 'recruitment'
    },
    {
      id: 'cv_search_bundle_3',
      name: 'CV Search Bundle (3 months)',
      description: 'Save 16% with 3 months of CV search access.',
      price: 249.00,
      icon: Search,
      features: ['Database access', 'Advanced search filters', 'Export CVs', '90-day access', '⭐ 16% savings'],
      color: 'bg-emerald-500',
      category: 'recruitment',
      badge: 'Popular'
    },
    {
      id: 'candidate_shortlist',
      name: 'AI Candidate Shortlisting',
      description: 'AI-powered pre-screening for up to 100 applications.',
      price: 149.00,
      icon: FileText,
      features: ['AI-powered screening', 'Up to 100 applications', 'Skills matching', '30-day access'],
      color: 'bg-pink-500',
      category: 'recruitment'
    },
    {
      id: 'direct_recruitment',
      name: 'Direct Recruitment Service',
      description: 'Expert recruitment team finds and screens candidates for you.',
      price: 299.00,
      icon: Users,
      features: ['Expert recruitment team', 'Full candidate screening', 'Up to 3 months support', 'White-glove service'],
      color: 'bg-indigo-500',
      category: 'recruitment'
    },
    {
      id: 'course_listing_standard',
      name: 'Standard Course Listing',
      description: 'Basic promoted placement for one course — affordable visibility for short courses and workshops.',
      price: 5.00,
      icon: Sparkles,
      features: ['One course', 'Basic promoted placement', '30-day duration'],
      color: 'bg-purple-400',
      category: 'course_promotion'
    },
    {
      id: 'course_listing_promoted',
      name: 'Promoted Course Listing',
      description: 'Higher placement in course search results for one course.',
      price: 10.00,
      icon: Star,
      features: ['One course', 'Higher search placement', '30-day duration'],
      color: 'bg-purple-600',
      category: 'course_promotion'
    },
    {
      id: 'course_listing_premium',
      name: 'Premium Featured Course Listing',
      description: 'Top placement and a Featured badge for one course.',
      price: 25.00,
      icon: Award,
      features: ['One course', 'Top search placement', 'Featured badge', '30-day duration'],
      color: 'bg-fuchsia-600',
      category: 'course_promotion',
      badge: 'Best for Trainers'
    }
  ];

  const services = baseServices
    .filter(s => !Object.keys(catalog).length || catalog[s.id])
    .map(s => {
      const c = catalog[s.id];
      if (!c) return s;
      return {
        ...s,
        name: c.name || s.name,
        description: c.description || s.description,
        price: typeof c.price === 'number' ? c.price : s.price,
        features: Array.isArray(c.features) && c.features.length ? c.features : s.features,
        badge: c.badge !== undefined ? c.badge : s.badge,
      };
    });

  const handleBankTransfer = async (service) => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/services');
      return;
    }
    setCheckingOut(`${service.id}-bank`);
    try {
      const quantity = quantities[service.id] || 1;
      const res = await axios.post(`${API_URL}/api/payments/bank-transfer/create`, {
        service_type: service.id,
        quantity: parseInt(quantity),
        discount_code: discountCode || undefined,
        country: localPrice.country,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setBankResult({ ...res.data, service: service.name });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create bank transfer payment');
    } finally {
      setCheckingOut(null);
    }
  };

  const handlePurchase = async (service) => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/services');
      return;
    }

    setCheckingOut(`${service.id}-stripe`);

    try {
      const quantity = quantities[service.id] || 1;
      const res = await axios.post(`${API_URL}/api/payments/purchase`, {
        service_type: service.id,
        quantity: parseInt(quantity),
        discount_code: discountCode || undefined,
        country: localPrice.country,
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setCheckingOut(null);
    }
  };

  const handlePayPalPurchase = async (service) => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/services');
      return;
    }

    setCheckingOut(`${service.id}-paypal`);

    try {
      const quantity = quantities[service.id] || 1;
      const res = await axios.post(`${API_URL}/api/payments/paypal/create-purchase-order`, {
        service_type: service.id,
        quantity: parseInt(quantity),
        discount_code: discountCode || undefined,
        country: localPrice.country,
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start PayPal checkout');
      setCheckingOut(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="purchase-services-page">
      <div className="bg-[#003366] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-white/10 text-white border-white/20 mb-4">One-Time Services</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Premium Services</h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Purchase additional services to boost your recruitment success
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {localPrice.error && <p role="alert" className="mb-6 text-center text-sm text-red-600">{localPrice.error}</p>}
        <Dialog open={!!bankResult} onOpenChange={(open) => { if (!open) setBankResult(null); }}>
          <DialogContent className="max-w-lg" data-testid="bank-transfer-result">
            {bankResult && (
              <>
                <DialogHeader>
                  <DialogTitle>Bank transfer details</DialogTitle>
                  <DialogDescription>{bankResult.service}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <p className="text-sm">Amount to pay: <strong>{formatCurrency(bankResult.amount, bankResult.currency)}</strong> (incl. VAT)</p>
                  <p className="text-sm">Payment reference (quote this exactly): <strong>{bankResult.reference}</strong></p>
                  <pre className="text-sm whitespace-pre-wrap bg-slate-50 border rounded p-3 max-h-64 overflow-auto">{bankResult.instructions}</pre>
                  <p className="text-xs text-muted-foreground">Your purchase is activated once our team confirms the funds have arrived.</p>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setBankResult(null)}>Close</Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>


        {/* Discount Code Section */}
        <Card className="mb-8 bg-blue-50 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Tag className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <Label className="font-semibold mb-2 block">Have a discount code?</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter discount code"
                    value={discountCode}
                    onChange={(e) => {
                      setDiscountCode(e.target.value.toUpperCase());
                      setDiscountMessage('');
                    }}
                    className="flex-1"
                  />
                  <Button variant="outline">Apply</Button>
                </div>
                {discountMessage && <p className="text-sm text-emerald-600 mt-2">{discountMessage}</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => {
            const Icon = service.icon;
            const stripeLoading = checkingOut === `${service.id}-stripe`;
            const paypalLoading = checkingOut === `${service.id}-paypal`;
            const bankLoading = checkingOut === `${service.id}-bank`;
            const anyLoading = stripeLoading || paypalLoading || bankLoading;
            const quantity = quantities[service.id] || 1;

            return (
              <Card
                key={service.id}
                className={`relative overflow-hidden hover:shadow-xl transition-all border-2 ${service.badge ? 'border-emerald-400 shadow-lg' : ''}`}
                data-testid={`service-card-${service.id}`}
              >
                {service.badge && (
                  <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    {service.badge}
                  </div>
                )}
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-12 h-12 rounded-xl ${service.color}/10 flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${service.color.replace('bg-', 'text-')}`} />
                    </div>
                    <Badge className="bg-slate-100 text-slate-700 capitalize">{service.category}</Badge>
                  </div>
                  <CardTitle className="text-lg">{service.name}</CardTitle>
                  <CardDescription className="text-xs">{service.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-[#003366]">{localPrice.format(service.price)}</span>
                      <span className="text-sm text-muted-foreground">+VAT</span>
                    </div>
                    {localPrice.isConverted && (
                      <p className="text-xs text-muted-foreground">Charged in {localPrice.currency}</p>
                    )}
                    <p className="text-xs text-muted-foreground">One-time payment</p>
                  </div>

                  {/* Quantity Selector */}
                  <div className="space-y-2 pb-3 border-b">
                    <Label htmlFor={`qty-${service.id}`} className="text-sm font-medium">Quantity</Label>
                    <Input
                      id={`qty-${service.id}`}
                      type="number"
                      min="1"
                      max="10"
                      value={quantity}
                      onChange={(e) => setQuantities({...quantities, [service.id]: e.target.value})}
                      className="h-9"
                    />
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Includes:</p>
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-xs">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    {stripeEnabled && (
                      <Button
                        onClick={() => handlePurchase(service)}
                        disabled={anyLoading || pricingUnavailable}
                        className="w-full bg-[#003366] hover:bg-[#003366]/90 h-10"
                      >
                        {stripeLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Zap className="w-4 h-4 mr-2" />
                            Pay with Card
                          </>
                        )}
                      </Button>
                    )}
                    {paypalEnabled && (
                      <Button
                        onClick={() => handlePayPalPurchase(service)}
                        disabled={anyLoading || pricingUnavailable}
                        variant="outline"
                        className="w-full h-10 border-[#0070ba] text-[#0070ba] hover:bg-[#0070ba]/5"
                      >
                        {paypalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Pay with PayPal
                      </Button>
                    )}
                    {bankEnabled && (
                      <Button
                        onClick={() => handleBankTransfer(service)}
                        disabled={anyLoading || pricingUnavailable}
                        variant="outline"
                        className="w-full h-10"
                        data-testid={`bank-transfer-${service.id}`}
                      >
                        {bankLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Pay by Bank Transfer
                      </Button>
                    )}
                    {!stripeEnabled && !paypalEnabled && !bankEnabled && (
                      <p className="text-xs text-center text-muted-foreground">Payments are temporarily unavailable. Please contact support.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Section */}
        <Card className="mt-12 bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg">About Our Services</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-2">Job Advert Credit</h4>
                <p className="text-sm text-muted-foreground">
                  Get premium visibility for your job postings. Advertise to millions of active job seekers with enhanced positioning and extended display time.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">CV Search</h4>
                <p className="text-sm text-muted-foreground">
                  Access our comprehensive CV database with advanced search filters. Find the perfect candidates using skills, experience, location, and more.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
